import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { EventsService } from './events.service';

interface UserProperties {
  user_type?: string;
  company_name?: string;
  subscription_plan?: string;
  user_role?: string;
  onboarding_completed?: boolean;
  total_agents_created?: number;
  preferred_language?: string;
}

interface JourneyEvent {
  eventName: string;
  params: any;
  timestamp: number;
}

@Injectable({
  providedIn: 'root'
})
export class UserJourneyAnalyticsService {
  private sessionStartTime: number;
  private currentRoute: string = '';
  private userJourney: JourneyEvent[] = [];
  private pageViewStartTime: number;
  
  // Key journey milestones
  private journeyMilestones = {
    firstLogin: false,
    profileCompleted: false,
    firstAgentCreated: false,
    firstChatSession: false,
    firstIntegration: false,
    firstTeamMemberInvited: false,
    subscriptionUpgraded: false
  };

  constructor(
    private eventsService: EventsService,
    private router: Router
  ) {
    this.sessionStartTime = Date.now();
    this.pageViewStartTime = Date.now();
    this.initializeRouteTracking();
  }

  /**
   * Initialize automatic route tracking
   */
  private initializeRouteTracking() {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.trackPageView(event.url);
    });
  }

  /**
   * Track page views with timing
   */
  private trackPageView(url: string) {
    const previousRoute = this.currentRoute;
    const timeOnPreviousPage = Date.now() - this.pageViewStartTime;
    
    // Log exit from previous page
    if (previousRoute) {
      this.logEvent('page_exit', {
        page_path: previousRoute,
        time_on_page_ms: timeOnPreviousPage,
        exit_to: url
      });
    }

    // Log entry to new page
    this.currentRoute = url;
    this.pageViewStartTime = Date.now();
    
    // Extract clean page name from URL
    const pageName = this.getPageNameFromUrl(url);
    
    this.eventsService.setScreenName(pageName);
    this.logEvent('page_view', {
      page_path: url,
      page_name: pageName,
      from_page: previousRoute,
      session_time_ms: Date.now() - this.sessionStartTime
    });
  }

  /**
   * Extract clean page name from URL
   */
  private getPageNameFromUrl(url: string): string {
    const segments = url.split('/').filter(s => s);
    if (segments.length === 0) return 'home';
    
    // Handle special cases
    if (segments[0] === 'job-offer-information' && segments[1]) return 'job_offer_details';
    if (segments[0] === 'agent-edit' && segments[1]) return 'agent_edit';
    if (segments[0] === 'applicant-information' && segments[1]) return 'applicant_details';
    
    return segments[0].replace(/-/g, '_');
  }

  /**
   * Track user authentication events
   */
  trackLogin(method: 'email' | 'google' | 'microsoft', userId: string, isFirstTime: boolean = false) {
    this.eventsService.setUserId(userId);
    
    this.logEvent('login', {
      method: method,
      is_first_time: isFirstTime
    });

    if (isFirstTime) {
      this.journeyMilestones.firstLogin = true;
      this.logEvent('first_login', {
        method: method
      });
    }

    // Start new session
    this.sessionStartTime = Date.now();
  }

  /**
   * Track signup events
   */
  trackSignup(
    method: 'email' | 'google' | 'microsoft',
    referralSource?: string,
    attribution?: {
      utm_source?: string;
      utm_medium?: string;
      utm_campaign?: string;
      utm_content?: string;
      utm_term?: string;
      utm_journey?: string;
      gclid?: string;
      fbclid?: string;
      campaign?: string;
      source?: string;
      landing_path?: string;
      signup_path?: string;
      campaign_id?: string;
      campaign_platform?: string;
    }
  ) {
    const payload: any = {
      method: method,
      referral_source: referralSource || attribution?.utm_source || attribution?.source || 'direct'
    };

    if (attribution) {
      if (attribution.utm_source) payload.utm_source = attribution.utm_source;
      if (attribution.utm_medium) payload.utm_medium = attribution.utm_medium;
      if (attribution.utm_campaign) payload.utm_campaign = attribution.utm_campaign;
      if (attribution.utm_content) payload.utm_content = attribution.utm_content;
      if (attribution.utm_term) payload.utm_term = attribution.utm_term;
      if (attribution.utm_journey) payload.utm_journey = attribution.utm_journey;
      if (attribution.gclid) payload.gclid = attribution.gclid;
      if (attribution.fbclid) payload.fbclid = attribution.fbclid;
      if (attribution.campaign) payload.campaign = attribution.campaign;
      if (attribution.source) payload.source = attribution.source;
      if (attribution.landing_path) payload.landing_path = attribution.landing_path;
      if (attribution.signup_path) payload.signup_path = attribution.signup_path;
      if (attribution.campaign_id) payload.campaign_id = attribution.campaign_id;
      if (attribution.campaign_platform) payload.campaign_platform = attribution.campaign_platform;
    }

    this.logEvent('sign_up', payload);
  }

  /**
   * Track logout
   */
  trackLogout() {
    const sessionDuration = Date.now() - this.sessionStartTime;
    
    this.logEvent('logout', {
      session_duration_ms: sessionDuration,
      pages_visited: this.userJourney.length
    });
    this.eventsService.clearUserContext();
  }

  /**
   * Track user profile events
   */
  trackProfileUpdate(fieldsUpdated: string[]) {
    this.logEvent('profile_updated', {
      fields_updated: fieldsUpdated,
      fields_count: fieldsUpdated.length
    });

    if (!this.journeyMilestones.profileCompleted && this.isProfileComplete()) {
      this.journeyMilestones.profileCompleted = true;
      this.logEvent('profile_completed', {});
    }
  }

  /**
   * Track agent-related events
   */
  trackAgentCreated(agentType: string, agentId: string, isFirst: boolean = false) {
    this.logEvent('agent_created', {
      agent_type: agentType,
      agent_id: agentId,
      is_first: isFirst
    });

    if (isFirst && !this.journeyMilestones.firstAgentCreated) {
      this.journeyMilestones.firstAgentCreated = true;
      this.eventsService.logFirstAgentCreated(agentType);
    }
  }

  trackAgentEdited(agentId: string, changedFields: string[]) {
    this.logEvent('agent_edited', {
      agent_id: agentId,
      changed_fields: changedFields,
      changes_count: changedFields.length
    });
  }

  trackAgentDeleted(agentId: string, agentType: string) {
    this.logEvent('agent_deleted', {
      agent_id: agentId,
      agent_type: agentType
    });
  }

  /**
   * Track chat/conversation events
   */
  trackChatStarted(agentId: string, conversationId: string) {
    this.logEvent('chat_started', {
      agent_id: agentId,
      conversation_id: conversationId
    });

    if (!this.journeyMilestones.firstChatSession) {
      this.journeyMilestones.firstChatSession = true;
      this.logEvent('first_chat_session', {
        agent_id: agentId
      });
    }
  }

  trackMessageSent(agentId: string, messageType: 'text' | 'voice' | 'file', messageLength?: number) {
    this.logEvent('message_sent', {
      agent_id: agentId,
      message_type: messageType,
      message_length: messageLength
    });
  }

  trackChatEnded(agentId: string, conversationId: string, duration: number, messageCount: number) {
    this.logEvent('chat_ended', {
      agent_id: agentId,
      conversation_id: conversationId,
      duration_ms: duration,
      message_count: messageCount
    });
  }

  /**
   * Track knowledge base events
   */
  trackKnowledgeBaseAccessed(section: string) {
    this.logEvent('knowledge_base_accessed', {
      section: section
    });
  }

  trackDocumentUploaded(documentType: string, fileSize: number) {
    this.logEvent('document_uploaded', {
      document_type: documentType,
      file_size_bytes: fileSize
    });
  }

  trackBookCreated(bookId: string, pageCount: number) {
    this.logEvent('book_created', {
      book_id: bookId,
      page_count: pageCount
    });
  }

  /**
   * Track job/recruiting events
   */
  trackJobOfferCreated(jobId: string, jobTitle: string, jobType: string) {
    this.logEvent('job_offer_created', {
      job_id: jobId,
      job_title: jobTitle,
      job_type: jobType
    });
  }

  trackApplicantViewed(applicantId: string, source: string) {
    this.logEvent('applicant_viewed', {
      applicant_id: applicantId,
      source: source
    });
  }

  trackApplicantStatusChanged(applicantId: string, oldStatus: string, newStatus: string) {
    this.logEvent('applicant_status_changed', {
      applicant_id: applicantId,
      old_status: oldStatus,
      new_status: newStatus
    });
  }

  /**
   * Track integration events
   */
  trackIntegrationConnected(integrationType: string) {
    this.logEvent('integration_connected', {
      integration_type: integrationType
    });

    if (!this.journeyMilestones.firstIntegration) {
      this.journeyMilestones.firstIntegration = true;
      this.logEvent('first_integration', {
        integration_type: integrationType
      });
    }
  }

  trackIntegrationDisconnected(integrationType: string) {
    this.logEvent('integration_disconnected', {
      integration_type: integrationType
    });
  }

  /**
   * Track team/collaboration events
   */
  trackTeamMemberInvited(role: string, inviteMethod: string) {
    this.logEvent('team_member_invited', {
      role: role,
      invite_method: inviteMethod
    });

    if (!this.journeyMilestones.firstTeamMemberInvited) {
      this.journeyMilestones.firstTeamMemberInvited = true;
      this.logEvent('first_team_member_invited', {
        role: role
      });
    }
  }

  trackTeamMemberJoined(role: string) {
    this.logEvent('team_member_joined', {
      role: role
    });
  }

  /**
   * Track subscription/payment events
   */
  trackSubscriptionViewed(planType: string) {
    this.logEvent('subscription_plan_viewed', {
      plan_type: planType
    });
  }

  trackUpgradeStarted(currentPlan: string, targetPlan: string, price: number) {
    this.eventsService.logUpgradeAttempt(targetPlan);
    
    this.logEvent('upgrade_started', {
      current_plan: currentPlan,
      target_plan: targetPlan,
      price: price
    });
  }

  trackSubscriptionUpgraded(oldPlan: string, newPlan: string, price: number) {
    this.journeyMilestones.subscriptionUpgraded = true;
    
    this.eventsService.logSubscriptionStart(newPlan);
    
    this.logEvent('subscription_upgraded', {
      old_plan: oldPlan,
      new_plan: newPlan,
      price: price
    });
  }

  /**
   * Track feature usage
   */
  trackFeatureUsed(featureName: string, featureCategory: string, additionalParams?: any) {
    this.logEvent('feature_used', {
      feature_name: featureName,
      feature_category: featureCategory,
      ...additionalParams
    });
  }

  /**
   * Track search events
   */
  trackSearch(searchTerm: string, searchContext: string, resultsCount: number) {
    this.logEvent('search', {
      search_term: searchTerm,
      search_context: searchContext,
      results_count: resultsCount
    });
  }

  /**
   * Track error events
   */
  trackError(errorType: string, errorMessage: string, errorContext: string) {
    this.logEvent('error_occurred', {
      error_type: errorType,
      error_message: errorMessage,
      error_context: errorContext
    });
  }

  /**
   * Track onboarding progress
   */
  trackOnboardingStep(stepName: string, stepNumber: number, isCompleted: boolean) {
    this.eventsService.logOnboardingStepCompleted(stepName, stepNumber);
    
    this.logEvent('onboarding_step', {
      step_name: stepName,
      step_number: stepNumber,
      is_completed: isCompleted
    });
  }

  getAllowedPlatforms(): string[] | null {
    const stored = localStorage.getItem('onboarding_allowed_platforms');
    if (stored) {
      try {
        const platforms = JSON.parse(stored);
        localStorage.removeItem('onboarding_allowed_platforms');
        return platforms;
      } catch (e) {
        console.error('Failed to parse allowed platforms:', e);
        localStorage.removeItem('onboarding_allowed_platforms');
        return null;
      }
    }
    return null;
  }

  trackOnboardingPlatformSelected(platform: string, wasAutoSelected: boolean, allowedPlatforms?: string[]) {
    this.logEvent('onboarding_platform_selected', {
      platform: platform,
      was_auto_selected: wasAutoSelected,
      was_filtered: allowedPlatforms ? allowedPlatforms.length < 4 : false,
      allowed_platforms: allowedPlatforms || ['agents', 'recruiting', 'hybrid', 'api']
    });
  }

  trackOnboardingCompleted(completionTime: number) {
    this.logEvent('onboarding_completed', {
      completion_time_ms: completionTime,
      milestones_completed: Object.values(this.journeyMilestones).filter(v => v).length
    });
  }

  /**
   * Set user properties for segmentation
   */
  setUserProperties(properties: UserProperties) {
    Object.entries(properties).forEach(([key, value]) => {
      if (value !== undefined) {
        this.eventsService.setUserProperty(key, String(value));
      }
    });
  }

  /**
   * Register PostHog super properties that attach to all subsequent events
   */
  registerSuperProperties(properties: { [key: string]: any }) {
    this.eventsService.registerSuperProperties(properties);
  }

  /**
   * Track custom business events
   */
  trackCustomEvent(eventName: string, params: any) {
    this.logEvent(eventName, params);
  }

  trackPricingPageView(utmParams?: {
    utm_journey?: string;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_content?: string;
    utm_term?: string;
  }) {
    const eventParams: any = { page: 'pricing' };
    if (utmParams) {
      if (utmParams.utm_journey) eventParams.user_journey = utmParams.utm_journey;
      if (utmParams.utm_source) eventParams.utm_source = utmParams.utm_source;
      if (utmParams.utm_medium) eventParams.utm_medium = utmParams.utm_medium;
      if (utmParams.utm_campaign) eventParams.utm_campaign = utmParams.utm_campaign;
      if (utmParams.utm_content) eventParams.utm_content = utmParams.utm_content;
      if (utmParams.utm_term) eventParams.utm_term = utmParams.utm_term;
    }
    this.logEvent('pricing_page_view', eventParams);
  }

  trackPlanSelected(planType: 'agents' | 'recruiting' | 'hybrid' | 'api', planName: string, price?: number) {
    this.logEvent('plan_selected', {
      plan_type: planType,
      plan_name: planName,
      price: price
    });
  }

  trackCheckoutStarted(planType: string, planName: string, price: number, billingPeriod: 'monthly' | 'yearly') {
    this.logEvent('checkout_started', {
      plan_type: planType,
      plan_name: planName,
      price: price,
      billing_period: billingPeriod
    });
  }

  trackDemoBooked(demoType: 'agents' | 'recruiting' | 'enterprise', source?: string) {
    this.logEvent('demo_booked', {
      demo_type: demoType,
      source: source || 'pricing_page'
    });
  }

  trackBillingPeriodChanged(newPeriod: 'monthly' | 'yearly') {
    this.logEvent('billing_period_changed', {
      new_period: newPeriod
    });
  }

  trackProductCategoryChanged(category: 'agents' | 'recruiting' | 'hybrid' | 'api') {
    this.logEvent('product_category_changed', {
      category: category
    });
  }

  trackTrialStarted(planType: string, trialDays: number) {
    this.logEvent('trial_started', {
      plan_type: planType,
      trial_days: trialDays
    });
  }

  trackCheckoutCompleted(success: boolean, planType: string, planName: string, price?: number, errorMessage?: string) {
    if (success) {
      this.logEvent('checkout_completed', { plan_type: planType, plan_name: planName, price: price });
      this.logEvent('purchase', { plan_type: planType, plan_name: planName, value: price, currency: 'EUR' });
    } else {
      this.logEvent('checkout_failed', { plan_type: planType, plan_name: planName, error_message: errorMessage });
    }
  }

  trackEmailVerified(method: 'link' | 'code' = 'link') {
    this.logEvent('email_verified', { method: method });
  }

  trackPasswordResetRequested(method: 'email' = 'email') {
    this.logEvent('password_reset_requested', { method: method });
  }

  trackPasswordResetCompleted(success: boolean, errorMessage?: string) {
    if (success) {
      this.logEvent('password_reset_completed', { success: true });
    } else {
      this.logEvent('password_reset_failed', { success: false, error_message: errorMessage });
    }
  }

  setAllowedPlatforms(platforms: string[]) {
    if (platforms && platforms.length > 0) {
      localStorage.setItem('onboarding_allowed_platforms', JSON.stringify(platforms));
      this.logEvent('signup_platform_filter', {
        platforms: platforms,
        platforms_count: platforms.length,
        is_filtered: true
      });
    }
  }

  /**
   * Get user journey summary
   */
  getUserJourneySummary() {
    return {
      session_duration_ms: Date.now() - this.sessionStartTime,
      pages_visited: this.userJourney.filter(e => e.eventName === 'page_view').length,
      total_events: this.userJourney.length,
      milestones_completed: this.journeyMilestones,
      journey_events: this.userJourney
    };
  }

  /**
   * Helper method to check if profile is complete
   */
  private isProfileComplete(): boolean {
    // Implement your logic to check if profile is complete
    // This is a placeholder - adjust based on your requirements
    return true;
  }

  /**
   * Central logging method
   */
  private logEvent(eventName: string, params: any) {
    const event: JourneyEvent = {
      eventName,
      params,
      timestamp: Date.now()
    };
    
    // Add to journey history
    this.userJourney.push(event);
    
    // Log to Firebase
    this.eventsService.logCustomEvent(eventName, params);
    
    // Console log in debug mode
    if (!environment.production) {
      console.log(`📊 Analytics Event: ${eventName}`, params);
    }
  }
}

// Import at the top of the file
import { environment } from 'src/environments/environment';